import { useMemo, useState, type FormEvent } from "react";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { Link } from "react-router-dom";

import logo from '../assets/img/logo.jpg';
import { ventrisConfig } from '../ventris.config';
import { focusFirstInvalidField } from '../accessibility/focus';

function ResetPassword() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validated, setValidated] = useState(false);
  const [step, setStep] = useState<"request" | "verify" | "password">("request");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const maskedEmail = useMemo(() => {
    const trimmed = email.trim();
    const domain = trimmed.split("@")[1];
    if (!domain) {
      return "***";
    }
    return `***@${domain}`;
  }, [email]);

  const passwordChecks = useMemo(() => {
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    return {
      hasMinLength,
      hasNumber,
      hasSymbol,
      hasUppercase,
      isValid: hasMinLength && hasNumber && hasSymbol && hasUppercase,
    };
  }, [password]);

  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const requestReset = async (username: string) => {
    const response = await fetch(ventrisConfig.url + '/api/reset', {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: username
      })
    });

    return response.json();
  };

  const verifyCode = async (username: string, verificationCode: string) => {
    const response = await fetch(ventrisConfig.url + '/api/verify', {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: username,
        mfa_code: verificationCode
      })
    });

    return response.json();
  };

  const updatePassword = async (username: string, newPassword: string, verificationCode: string) => {
    const response = await fetch(ventrisConfig.url + '/api/password', {
      method: 'post',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: username,
        password: newPassword,
        mfa_code: verificationCode
      })
    });

    return response.json();
  };

  const handleRequestSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      try {
        const data = await requestReset(email);
        if (data.success) {
          setValidated(false);
          setStep("verify");
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleVerifySubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      try {
        const data = await verifyCode(email, code);
        if (data.success) {
          setValidated(false);
          setStep("password");
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false || !passwordChecks.isValid || !passwordsMatch) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false && passwordChecks.isValid && passwordsMatch) {
      setValidated(true);
      try {
        const data = await updatePassword(email, password, code);
        if (data.success && data.token) {
          const token = data.token;
          sessionStorage.setItem("token", token);
          const accountResponse = await fetch(ventrisConfig.url + '/api/account', {
            method: 'get',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token
            }
          });
          const accountData = await accountResponse.json();
          let currentUser: any = accountData;
          sessionStorage.setItem("portal", currentUser.role.toLowerCase());
          sessionStorage.setItem("current_user", JSON.stringify(currentUser));
          document.location.href = "/";
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleSendNewCode = async () => {
    try {
      await requestReset(email);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Container fluid as="main" id="main-content" tabIndex={-1} className="login-wrapper">
      <Row className="login-row">
        <Col className="login-card">
          <div className="login-card-inner">
            <div className="login-card-header">
              <img src={logo} alt="Ventris Learning Logo" className="login-logo" />
            </div>
            <div className="login-card-divider" />
            <div className="login-card-body">
              <h1 className="login-title">Reset Password</h1>
              {step === "verify" ? (
                <>
                  <p className="login-subtitle" id="reset-code-instructions">
                    Please enter the code that was emailed to {maskedEmail}.
                    <br />
                    This code will expire in 5 minutes.
                  </p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleVerifySubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="verificationCode">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">VERIFICATION CODE</Form.Label>
                        <span className="login-label-hint">
                          Code expired?{" "}
                          <button type="button" className="login-link login-link-button" onClick={handleSendNewCode}>Send new code.</button>
                        </span>
                      </div>
                      <Form.Control
                        required
                        type="text"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        aria-describedby="reset-code-instructions"
                        placeholder="Enter the 6-Digit Code"
                        value={code}
                        onChange={(event) => setCode(event.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">Please enter a code.</Form.Control.Feedback>
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${code ? " login-btn-primary-active" : ""}`}
                        disabled={!code}
                      >
                        Verify Code
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : step === "password" ? (
                <>
                  <p className="login-subtitle" id="reset-password-instructions">Please choose a new, strong password.</p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handlePasswordSubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="newPassword">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">PASSWORD</Form.Label>
                        <span className="login-label-hint">Must meet minimum requirements below.</span>
                      </div>
                      <div className="login-password-field">
                        <Form.Control
                          required
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          aria-describedby="reset-password-instructions reset-password-requirements"
                          aria-invalid={password.length > 0 && !passwordChecks.isValid}
                          placeholder="Choose a Password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                        />
                        <button
                          type="button"
                          className="login-password-toggle"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5C6.5 5 2 12 2 12s4.5 7 10 7 10-7 10-7-4.5-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                          </svg>
                        </button>
                      </div>
                      <Form.Control.Feedback type="invalid">Please enter a password.</Form.Control.Feedback>
                      <div className="login-password-requirements" id="reset-password-requirements">
                        <span className={`login-requirement${passwordChecks.hasMinLength ? " login-requirement--met" : ""}`}>8 Characters</span>
                        <span className={`login-requirement${passwordChecks.hasNumber ? " login-requirement--met" : ""}`}>1 Number</span>
                        <span className={`login-requirement${passwordChecks.hasSymbol ? " login-requirement--met" : ""}`}>1 Symbol</span>
                        <span className={`login-requirement${passwordChecks.hasUppercase ? " login-requirement--met" : ""}`}>1 Capital Letter</span>
                      </div>
                    </Form.Group>
                    <Form.Group className="mb-2 ventris-form-element" controlId="confirmPassword">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">CONFIRM PASSWORD</Form.Label>
                        <span className="login-label-hint">Passwords must match.</span>
                      </div>
                      <div className="login-password-field">
                        <Form.Control
                          required
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                          placeholder="Re-enter your Password"
                          value={confirmPassword}
                          onChange={(event) => setConfirmPassword(event.target.value)}
                        />
                        <button
                          type="button"
                          className="login-password-toggle"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 5C6.5 5 2 12 2 12s4.5 7 10 7 10-7 10-7-4.5-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                          </svg>
                        </button>
                      </div>
                      <Form.Control.Feedback type="invalid">Please confirm your password.</Form.Control.Feedback>
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${passwordChecks.isValid && passwordsMatch ? " login-btn-primary-active" : ""}`}
                        disabled={!passwordChecks.isValid || !passwordsMatch}
                      >
                        Continue
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : (
                <>
                  <p className="login-subtitle">
                    Please enter your username for the account you'd like to reset the password for.
                  </p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleRequestSubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="email">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">USERNAME</Form.Label>
                        <span className="login-label-hint" id="reset-username-hint">Your username is your email address.</span>
                      </div>
                      <Form.Control required type="email" autoComplete="username" aria-describedby="reset-username-hint" placeholder="Enter Username" value={email} onChange={(event) => setEmail(event.target.value)} />
                      <Form.Control.Feedback type="invalid">Please enter an email.</Form.Control.Feedback>
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${email ? " login-btn-primary-active" : ""}`}
                        disabled={!email}
                      >
                        Continue
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              )}
            </div>
            <div className="login-card-divider" />
            <div className="login-card-footer">
              <span className="login-help-text">
                <strong>Need help?</strong> Contact us by email at{" "}
                <a className="login-help-link" href="mailto:help@ventrislearning.com">help@ventrislearning.com</a>{" "}
                or by phone at{" "}
                <a className="login-help-link" href="tel:800-555-1234">800-555-1234</a>.
              </span>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default ResetPassword;
